/**
 * Email Event Component
 *
 * Handles ahoy_email_sent and ahoy_email_received events.
 *
 * Display pattern:
 * - Header: "**Actor** sent email: **Subject**" / "**Actor** received email: **Subject**"
 * - From / To metadata
 * - Collapsed: 3-line snippet with blur-fade
 * - Expanded: full email body text
 *
 * Actor fallback: synced-mailbox and test-provider events often carry a
 * source the viewer can't resolve (the ingesting integration entity). When the
 * resolved actor is a generic placeholder, the header falls back to the email
 * envelope's sender — "You" for the viewer's own address — instead of
 * surfacing "Unknown" or the provider's internal name.
 */

import type { ReactNode } from 'react';
import { Fragment, useCallback, useMemo } from 'react';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { CollapsibleContent } from '@/lib/base/collapsible-content';
import { EmailAddressTag } from '@/lib/crm/components/EmailAddressTag';
import { EmailThreadVisibilityBadge } from '@/lib/crm/components/EmailThreadVisibilityBadge';
import { type AccessSummary, getPropertyValue } from '@/types/crm';

import { useOpenSlideout } from '../../../hooks/useOpenSlideout';
import { useSlideoutStack } from '../../../hooks/useSlideoutStack';
import { getBookingTimelineLabel } from '../booking-labels';
import {
  ActorName,
  bold,
  inlineButton,
  InlineText,
  TimelineEvent,
} from '../components/shared';
import { useViewStateLookups } from '../context/TimelineLookupsContext';
import { TIMELINE_DESIGN_TOKENS } from '../design-tokens';
import { senderLabelForPayload } from '../threadMessages';
import type { EmailPayload } from '../types';
import { EVENT_TYPES } from '../types';
import { useEventFormatters } from '../useEventFormatters';
import { EmailReplyButton } from './EmailReplyButton';
import type { EventComponentProps } from './types';

/**
 * Actor names that mean "we couldn't resolve who did this" rather than a real
 * identity — the sender-envelope fallback replaces exactly these.
 */
const UNRESOLVED_ACTOR_NAMES = new Set(['Unknown', 'Synced mailbox']);

export function EmailEvent({ event }: EventComponentProps) {
  const { topId } = useSlideoutStack();
  const openSlideout = useOpenSlideout();
  const { getActorInfo } = useEventFormatters();
  const { lookups } = useViewStateLookups();
  const { user } = useAuthInfo();

  const accessSummary = useMemo((): AccessSummary | undefined => {
    const threadObj = lookups.entities[event.entity_id];
    if (!threadObj?.associations) {
      return undefined;
    }
    const integrationAssoc = threadObj.associations.find(
      a => a.association_type_name === 'ahoy_email_thread_to_integration'
    );
    if (!integrationAssoc) {
      return undefined;
    }
    const integrationObj = lookups.entities[integrationAssoc.target_entity_id];
    const raw = getPropertyValue<string>(
      integrationObj?.properties?.ahoy_default_data_visibility
    );
    if (raw === 'org' || raw === 'private') {
      return {
        privacy_state: raw,
        access_rules: [],
        settings_link: null,
        viewer_is_owner: false,
      };
    }
    return undefined;
  }, [lookups.entities, event.entity_id]);

  const handleSubjectClick = useCallback(() => {
    if (topId === event.entity_id) {
      return; // No-op: thread is already the top slideout
    }
    openSlideout(event.entity_id);
  }, [topId, event.entity_id, openSlideout]);

  // Resolve addresses to CRM entity names through the view-state lookups so
  // the fallback label matches entity badges elsewhere in the product.
  const resolveEmailName = useCallback(
    (email: string): string | undefined => {
      const entityId = lookups.emails?.[email];
      if (!entityId) {
        return undefined;
      }
      return lookups.entities[entityId]?.ahoy_display_name ?? undefined;
    },
    [lookups]
  );

  const bookingLabel = getBookingTimelineLabel(event);

  if (bookingLabel !== null) {
    return (
      <TimelineEvent ariaLabel={bookingLabel}>
        <TimelineEvent.Icon event={event} />
        <TimelineEvent.Content
          eventId={event.event_id}
          header={<InlineText>{bold(bookingLabel)}</InlineText>}
          timestamp={event.occurred_at}
        />
      </TimelineEvent>
    );
  }

  const actor = getActorInfo(event);
  const payload = event.payload as EmailPayload | undefined;
  const isSent = event.event_type_name === EVENT_TYPES.EMAIL_SENT;
  const isHarbour = payload?.provider === 'harbour';

  const subject = payload?.subject || 'email';
  const fromAddress = payload?.from;
  const toAddresses = payload?.to;

  // Envelope-derived sender label, used when the event-source actor doesn't
  // resolve to a real identity ("Unknown", "Synced mailbox") or when the
  // provider name would otherwise leak into the copy (harbour).
  const senderLabel = senderLabelForPayload(
    payload,
    user?.email,
    resolveEmailName
  );
  const actorUnresolved = UNRESOLVED_ACTOR_NAMES.has(actor.name);

  // Body content shown in the timeline event. Prefer `body_text`, then
  // `body`, then `snippet` / `body_preview`. HTML-only emails (e.g.
  // transactional notifications from Figma, Google Docs) ship
  // `body_text` as a single whitespace character — truthy but visually
  // empty. Treat whitespace-only as absent.
  const bodyText = payload?.body_text;
  const rawFullBody = bodyText?.trim() ? bodyText : payload?.body;
  const fullBody = rawFullBody?.trim() ? rawFullBody : undefined;
  const rawSnippet = payload?.snippet || payload?.body_preview;
  const snippet = rawSnippet?.trim() ? rawSnippet : undefined;
  // <CollapsibleContent> auto-detects whether the visible text actually
  // overflows the collapsed height and only renders the toggle when it
  // does — no need for the manual length comparison the old hand-rolled
  // version used.
  const bodyContent = fullBody || snippet;

  let ariaLabel: string;
  let headerContent: ReactNode;
  if (isSent) {
    if (actorUnresolved && senderLabel) {
      ariaLabel = `${senderLabel} sent email: ${subject}`;
      headerContent = <>{bold(senderLabel)} sent email</>;
    } else {
      ariaLabel = `${actor.name} sent email: ${subject}`;
      headerContent = (
        <>
          <ActorName actor={actor} /> sent email
        </>
      );
    }
  } else if ((isHarbour || actorUnresolved) && senderLabel) {
    // Inbound with an unresolvable actor: name the message's author from the
    // envelope — "David Abernathy sent email" — instead of "Harbour email
    // received" / "Unknown".
    ariaLabel = `${senderLabel} sent email: ${subject}`;
    headerContent = <>{bold(senderLabel)} sent email</>;
  } else if (isHarbour || actorUnresolved) {
    ariaLabel = `Email received: ${subject}`;
    headerContent = <>Email received</>;
  } else {
    ariaLabel = `${actor.name} received email: ${subject}`;
    headerContent = (
      <>
        <ActorName actor={actor} /> received email
      </>
    );
  }

  const metaTextClass = `${TIMELINE_DESIGN_TOKENS.typography.subItem} text-secondary`;

  return (
    <TimelineEvent ariaLabel={ariaLabel}>
      <TimelineEvent.Icon event={event} />
      <TimelineEvent.Content
        eventId={event.event_id}
        header={
          <InlineText>
            {headerContent}
            {payload?.subject && (
              <>: {inlineButton(subject, handleSubjectClick)}</>
            )}
            <EmailThreadVisibilityBadge access_summary={accessSummary} />
          </InlineText>
        }
        timestamp={event.occurred_at}
      >
        <div className='mt-2 flex flex-col gap-1'>
          {/* From / To */}
          {fromAddress && (
            <div className={metaTextClass}>
              From: <EmailAddressTag address={fromAddress} />
            </div>
          )}
          {toAddresses && toAddresses.length > 0 && (
            <div className={metaTextClass}>
              To:{' '}
              {toAddresses.map((addr, i) => (
                // eslint-disable-next-line react/no-array-index-key -- recipient list is from immutable event payload; duplicates are possible so addr alone is not unique
                <Fragment key={`${addr}-${i}`}>
                  {i > 0 && ', '}
                  <EmailAddressTag address={addr} />
                </Fragment>
              ))}
            </div>
          )}

          {/* Body — clipped to ~3 lines (63px) by default with the
              shared <CollapsibleContent>, which renders the fade
              gradient + "Show more / Show less" toggle only when the
              content actually overflows the cap. */}
          {bodyContent && (
            <div className='mt-1'>
              <CollapsibleContent maxCollapsedHeight={63}>
                <div className={`${metaTextClass} whitespace-pre-wrap`}>
                  {bodyContent}
                </div>
              </CollapsibleContent>
            </div>
          )}
          {!isSent && (
            <EmailReplyButton
              entityId={event.entity_id}
              replyToEventId={event.event_id}
              payload={payload}
              className='mt-2'
            />
          )}
        </div>
      </TimelineEvent.Content>
    </TimelineEvent>
  );
}
